import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Input, Table, Spin, notification, Modal } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getAllStates, deleteState } from '../../services/statesApi';
import { getStatesTree } from '../../services/adminApi';
import { useAuth } from '../../hooks/auth';
import StateModal from '../../components/StateModal';
import { State as BaseState } from '../../@types/state';

interface State extends BaseState {
    isLp?: boolean;
}

interface TreeNode {
    label: string;
    value: number;
    disabled?: boolean;
    children?: TreeNode[];
}

const States: React.FC = () => {
    const [states, setStates] = useState<State[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingState, setEditingState] = useState<State | null>(null);
    const [search, setSearch] = useState('');
    const [statesForModal, setStatesForModal] = useState<TreeNode[]>([]);

    useEffect(() => {
        loadStates();
        loadStatesTree();
    }, []);

    const loadStates = async () => {
        setIsLoading(true);
        try {
            const result = await getAllStates('F');
            setStates(result);
        } catch (error) {
            notification.error({ message: 'Ошибка при загрузке филиалов' });
        } finally {
            setIsLoading(false);
        }
    };

    const loadStatesTree = async () => {
        try {
            const result = await getStatesTree();
            setStatesForModal(processTreeData(result));
        } catch (error) {
            notification.error({ message: 'Ошибка при загрузке дерева филиалов' });
        }
    };

    const processTreeData = (tree: any[]): TreeNode[] => {
        return tree.map(node => ({
            ...node,
            disabled: node.type !== 'FS',
            children: node.children ? processTreeData(node.children) : [],
        }));
    };

    const handleStateSuccess = (parentId: number, newState: State) => {
        loadStates();
        loadStatesTree();
        setEditingState(null);
    };

    const handleEdit = (state: State) => {
        setEditingState(state);
        setIsModalOpen(true);
    };

    const handleCreate = () => {
        setEditingState(null);
        setIsModalOpen(true);
    };

    const handleDelete = (state: State) => {
        Modal.confirm({
            title: 'Удаление филиала',
            content: `Вы уверены, что хотите удалить филиал "${state.name}"?`,
            onOk: () => handleDeleteConfirm(state),
            okText: 'Да',
            cancelText: 'Отмена',
            maskClosable: true,
        });
    };

    const handleDeleteConfirm = async (state: State) => {
        try {
            await deleteState(state.id);
            await loadStates();
            notification.success({ message: 'Филиал успешно удален' });
        } catch (error) {
            notification.error({ message: 'Ошибка при удалении филиала' });
        }
    };

    const columns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: State, b: State) => a.name.localeCompare(b.name),
        },
        {
            title: 'Тип',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: State) => (
                <Row gutter={8}>
                    <Col>
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            type="text"
                        />
                    </Col>
                    <Col>
                        <Button
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                            type="text"
                        />
                    </Col>
                </Row>
            ),
        }
    ];

    const filteredStates = states.filter(state =>
        state.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Spin spinning={isLoading}>
            <Row justify="space-between" align="middle" style={{ marginTop: 40 }}>
                <Col>
                    <div style={{ fontSize: 25 }}>Филиалы</div>
                </Col>
                <Col>
                    <Button type="primary" onClick={handleCreate}>Создать</Button>
                </Col>
            </Row>

            <Row style={{ marginTop: 20 }}>
                <Col span={24}>
                    <Input
                        placeholder="Поиск по названию"
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ marginBottom: 16 }}
                    />
                </Col>
                <Col span={24}>
                    <div style={{ fontSize: 14, marginBottom: 10 }}>Всего филиалов: {filteredStates.length}</div>
                    <Table
                        scroll={{ x: 300 }}
                        columns={columns}
                        dataSource={filteredStates}
                        loading={isLoading}
                        pagination={{
                            showSizeChanger: true,
                            locale: {
                                items_per_page: 'стр',
                                jump_to: 'Перейти',
                                jump_to_confirm: 'OK',
                                page: 'стр',
                            }
                        }}
                    />
                </Col>
            </Row>

            <StateModal
                visible={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                states={statesForModal}
                editingState={editingState}
                onSuccess={handleStateSuccess}
            />
        </Spin>
    );
};

export default States; 